<script setup lang="ts">
import { useAddressesStore } from '@/store/addresses/addresses'
import { useBasketStore } from '@/store/basket/basket'
import { useBasketPaymentMethodsStore } from '@/store/basket/basket-payment-methods'
import { useBasketPointStore } from '@/store/basket/basket-point'

import {
  DeliveryMethods,
  SetDeliveryMethodPayload,
} from '@/api/modules/basket/types'
import { isAddressClient } from '@/types/store/basket/index'

import BasketAddressesTabs from '@/components/Basket/basket-addresses/BasketAddressesTabs.vue'
import BasketAddressesWindows from '@/components/Basket/basket-addresses/BasketAddressesWindows.vue'
import BasketTitle from '@/components/Basket/basket-utils/BasketTitle.vue'

const { $sentryCaptureException } = useNuxtApp()

const addressesStore = useAddressesStore()
const basketStore = useBasketStore()
const basketPaymentMethodsStore = useBasketPaymentMethodsStore()
const pointStore = useBasketPointStore()

const isUpdatePoint = computed(() => basketStore.isExtraBasket)

// Следим за флагом и обновляем список точек
watch(isUpdatePoint, () => pointStore.getPoints())

onMounted(async () => {
  await Promise.all([pointStore.getPoints(), addressesStore.getAddresses()])

  if (pointStore.error || addressesStore.addresses.error)
    $sentryCaptureException(pointStore.error || addressesStore.addresses.error)

  // Если установлен способ доставки pickup/hall, а точки отключены
  if (!pickupPoints.value.length && !hallPoints.value.length) {
    basketStore.setDeliveryMethodInState(DeliveryMethods.COURIER)
  }

  // @ts-ignore
  window.carrotquest?.track('Выбрана доставка', {})
})

const deliveryMethod = computed(() => basketStore.data.delivery.method)

const courierPoints = computed(() => addressesStore.addresses.data)
const pickupPoints = computed(() => pointStore.pickupPoints)
const hallPoints = computed(() => pointStore.hallPoints)
// TODO [basket-refactoring]
// архитектурная проблема, что "самовывоз" и "в ресторане" - это одна модель и список, а "Доставка" - другая/другой
// Поэтому приходится везде писать проверки на тип DeliveryMethod
const allPoints = computed(() => {
  return {
    [DeliveryMethods.COURIER]: courierPoints.value,
    [DeliveryMethods.PICKUP]: pickupPoints.value,
    [DeliveryMethods.HALL]: hallPoints.value,
  }
})

/* Windows */
const selectedAddress = computed(() => {
  const selectedAddressId =
    basketStore.data.delivery.addresses[deliveryMethod.value] || null

  // Ищем адрес целиком
  return allPoints.value[deliveryMethod.value].find(
    ({ id }) => id === selectedAddressId,
  )
})

const deliveryMethodChanged = () => {
  // Устанавливаем первый из списка оплат
  if (basketPaymentMethodsStore.data.length)
    basketStore.setSelectedPaymentMethod(basketPaymentMethodsStore.data[0])
}

const setDeliveryMethod = async (newDeliveryMethod: DeliveryMethods) => {
  const points = allPoints.value[newDeliveryMethod]

  let deliveryMethodPayload: SetDeliveryMethodPayload = {
    delivery_method: newDeliveryMethod,
    point_id: points[0]?.id,
  }

  // Для доставки формируем данные для бэкэнда
  const firstAddress = points[0]
  if (
    newDeliveryMethod === DeliveryMethods.COURIER &&
    isAddressClient(firstAddress)
  ) {
    const isAllowedAddress =
      !firstAddress ||
      !firstAddress.is_in_delivery_area ||
      firstAddress?.allow_redirect

    // Для доставки курьером проверяем существует ли адрес в зоне доставки, и соответсвует ли городу
    if (isAllowedAddress) return

    deliveryMethodPayload = {
      delivery_method: newDeliveryMethod,
    }
    if (firstAddress.is_in_delivery_area) {
      deliveryMethodPayload.address_id = firstAddress.id
    }
  }

  await basketStore.setDeliveryMethod({
    ...deliveryMethodPayload,
  })

  deliveryMethodChanged()

  const carrotquestTracks = {
    courier: 'Выбрана доставка',
    pickup: 'Выбран самовывоз',
    hall: 'Выбран заказ в зал',
  }

  // @ts-ignore
  window.carrotquest?.track(carrotquestTracks[newDeliveryMethod], {})
}

/* Tabs */
const isShowPickupTab = computed(() => Boolean(pickupPoints.value.length))

const isShowHallTab = computed(() => Boolean(hallPoints.value.length))

const setModelValueDeliveryMethod = (value: DeliveryMethods) => {
  basketStore.setDeliveryMethodInState(value)

  setDeliveryMethod(value)
}
</script>

<template>
  <section id="addresses" class="addresses">
    <basket-title title="Адрес" />

    <basket-addresses-tabs
      :model-value="deliveryMethod"
      :is-show-pickup-tab="isShowPickupTab"
      :is-show-hall-tab="isShowHallTab"
      @update:model-value="setModelValueDeliveryMethod"
    />

    <basket-addresses-windows
      class="addresses__windows"
      :delivery-method="deliveryMethod"
      :selected-address="selectedAddress"
      @delivery-method-changed="deliveryMethodChanged"
      @select-delivery-courier="setDeliveryMethod(DeliveryMethods.PICKUP)"
    />
  </section>
</template>

<style lang="scss" scoped>
.addresses__windows {
  margin-top: 24px;
}
</style>
